import { Box, Button, TextField, Tooltip } from "@mui/material"
import { useState } from "react"
import { toast } from "react-toastify"
import { ContainerHeader, ContainerPage } from "./styles"
import { PageTitle } from "components"
import { replenishmentService } from "services/replenishmentService"
import { useTableParams } from "hooks/useTableParams"
import { Table } from "shared"
import { IReplenishmentDataResponse, IReplenishmentEditQuantity } from "interfaces/IReplenishment"
import { ModalEditReplenishment } from "components/ModalEditReplenishment"

const Replenishment = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isOpenEditModal, setIsOpenEditModal] = useState(false)
  const [editModalData, setEditModalData] = useState<IReplenishmentDataResponse>(
    {} as IReplenishmentDataResponse,
  )
  const [scheduledDate, setScheduledDate] = useState(getInitialDeliveryDate())
  const { count, data, params, refresh, onChange, status } = useTableParams({
    service: (params: any) => replenishmentService.getReplenishments(params),
  })

  function getInitialDeliveryDate() {
    const now = new Date()
    const hours = now.getHours()

    if (hours >= 7) {
      now.setDate(now.getDate() + 1)
    }

    const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0]

    return localDate
  }

  const handleSearchByDate = (date: any) => {
    setScheduledDate(date)
    onChange({ ...params, scheduledDate: date })
  }

  const handleSkuSearch = (skuName: any) => {
    onChange({ ...params, skuName })
  }

  const isPastInitialDate = (date: string) => {
    const initialDate = getInitialDeliveryDate()
    return new Date(date) < new Date(initialDate)
  }

  const handleRevertReplenishment = async (id: number) => {
    try {
      setIsLoading(true)
      await replenishmentService.deleteReplenishment(id)
      refresh()
      toast("abastecimento desconsiderado com sucesso!", { type: "success" })
    } catch (error: any) {
      toast(
        error?.message ?? "Ocorreu um erro ao excluir abastecimento, dá um alô pro time de tech.",
        { type: "error" },
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenEditModal = (rowData: any) => {
    setEditModalData(rowData?.data)
    setIsOpenEditModal(true)
  }

  const handleCloseEditModal = () => {
    setEditModalData({} as IReplenishmentDataResponse)
    setIsOpenEditModal(false)
  }

  const handleEditReplenishment = async (newQuantity: number) => {
    try {
      setIsLoading(true)
      await replenishmentService.editReplenishmentQuantity(editModalData?.id, {
        newQuantity,
        marketNumber: editModalData?.marketNumber,
        isCorrection: true,
      })
      refresh()
      handleCloseEditModal()
      toast("abastecimento editado com sucesso!", { type: "success" })
    } catch (error: any) {
      toast(
        error?.message ?? "Ocorreu um erro ao editar abastecimento, dá um alô pro time de tech.",
        { type: "error" },
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <ContainerPage style={{ height: "auto" }}>
        <ContainerHeader component="header" style={{ display: "flex", flexDirection: "column" }}>
          <PageTitle>Abastecimentos</PageTitle>
          <h2 style={{ marginTop: "10px", fontSize: "1.25rem", color: "#637381", fontWeight: 400 }}>
            Aqui você encontra os abastecimentos realizados.
          </h2>
        </ContainerHeader>

        <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: "20px" }}>
          <TextField
            type="date"
            label="Data de Entrega"
            variant="outlined"
            value={scheduledDate}
            onChange={e => handleSearchByDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            style={{ marginRight: "20px" }}
          />
          <TextField
            label="Buscar SKU"
            variant="outlined"
            onChange={e => handleSkuSearch(e.target.value)}
            style={{ marginRight: "20px" }}
          />
        </Box>

        <Table
          columns={[
            {
              style: { fontSize: "1.2rem" },
              field: "updated",
              label: "Horário",
              renderCell: (row: any) => {
                const updated = row?.data?.updated

                const formatedHours = updated
                  ? new Date(updated).toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })
                  : "--:--:--"

                return <span>{formatedHours}</span>
              },
            },
            {
              style: { fontSize: "1.2rem" },
              field: "skuName",
              label: "Produto",
            },
            {
              style: { fontSize: "1.2rem" },
              field: "batchId",
              label: "Lote",
            },
            {
              style: { fontSize: "1.2rem" },
              field: "quantity",
              label: "Quantidade",
            },
            {
              style: { fontSize: "1.2rem" },
              field: "marketNumber",
              label: "Mercado",
            },
            {
              style: { fontSize: "1.2rem" },
              field: "replenisher",
              label: "Abastecedor(a)",
            },
            {
              style: { fontSize: "1.2rem" },
              field: "type",
              label: "Movimentação",
              renderCell: (row: any) => {
                const movimentacao =
                  row?.data?.type === "stock_move"
                    ? "Estoque"
                    : row?.data?.type === "migrate_sku"
                      ? "Migração"
                      : "Realocado"

                return <span>{movimentacao}</span>
              },
            },
            {
              style: { fontSize: "1.2rem" },
              field: "id",
              label: "",
              renderCell: (row: any) => {
                return (
                  <Tooltip
                    title={
                      isPastInitialDate(scheduledDate)
                        ? "Não é possível editar, registro não é da operação atual."
                        : ""
                    }
                    arrow
                    disableInteractive
                  >
                    <span>
                      {" "}
                      <Button
                        variant="contained"
                        style={{ background: "#F8974C", color: "#080707" }}
                        onClick={() => handleOpenEditModal(row)}
                        disabled={isPastInitialDate(scheduledDate) || isLoading}
                      >
                        Editar
                      </Button>
                    </span>
                  </Tooltip>
                )
              },
            },
            {
              style: { fontSize: "1.2rem" },
              field: "id",
              label: "",
              renderCell: (row: any) => {
                return (
                  <Tooltip
                    title={
                      isPastInitialDate(scheduledDate)
                        ? "Não é possível excluir, registro não é da operação atual."
                        : ""
                    }
                    arrow
                    disableInteractive
                  >
                    <span>
                      {" "}
                      <Button
                        variant="contained"
                        style={{ background: "#f26357", color: "#080707" }}
                        onClick={() => handleRevertReplenishment(row?.data?.id)}
                        disabled={isPastInitialDate(scheduledDate) || isLoading}
                      >
                        Excluir
                      </Button>
                    </span>
                  </Tooltip>
                )
              },
            },
          ]}
          data={data}
          params={params}
          onChange={onChange}
          refresh={refresh}
          count={count}
          status={status}
          rowStyle={() => ({ ".MuiTableCell-root": { fontSize: "1.1rem" } })}
        />
      </ContainerPage>
      <ModalEditReplenishment
        open={isOpenEditModal}
        isLoading={isLoading}
        handleClose={handleCloseEditModal}
        handleConfirm={handleEditReplenishment}
        item={editModalData}
      />
    </>
  )
}

export default Replenishment
